import { CONFIG_SERVER, DEFAULT_URL_UPLOAD, MIME_TO_EXTENSION } from '@vindr/services/constants/common.constant';
import keycloak from '@vindr/services/keycloak';
import { getExtensionFileName } from '@vindr/utils/converter';
import { cleanDiacritics, uuidv4 } from '@vindr/utils/helpers';
import { stopReload } from '@vindr/utils/helpers/common.helper';
import pLimit from 'p-limit';
import { Dispatch } from 'react';
import S3Uploader, { S3UploaderConfig } from '@vindr/services/S3Uploader';
import Uploader, { UploaderConfig } from '../../../StudyFileUpload/Uploader';
import { clearListCache } from '../../Webcam/helper/storeIndexDB';
import {
  CaptureManagerAction,
  CaptureManagerContextProps,
  CaptureManagerProps,
} from '../context/type';
import { isPublicPortal } from '@vindr/utils/helpers';
const limit = pLimit(6);

const uploadFiles = async ({
  params,
  actionUpload,
  selected,
  storeFiles,
  dispatch,
  processingList,
}: {
  params: CaptureManagerProps['params'];
  actionUpload: CaptureManagerProps['actionUpload'];
  selected: CaptureManagerContextProps['selected'];
  storeFiles: CaptureManagerContextProps['storeFiles'];
  dispatch: Dispatch<CaptureManagerAction>;
  processingList: CaptureManagerContextProps['processingList'];
}): Promise<false | true> => {
  let currentParams = params;
  if (actionUpload && !currentParams) {
    try {
      const response = await actionUpload?.();
      if (!response) return false;

      currentParams = response;
    } catch (err) {
      console.log(err);
      return false;
    }
  }

  window.addEventListener('beforeunload', stopReload, { capture: true });

  if (!selected?.length) return;

  //? Only re-upload ready and exception file
  const selectedValidList = selected
    .map((it) => {
      return storeFiles.find((f) => f.id === it.id);
    })
    .filter((file) => {
      const process = processingList.get(file.id);

      return (['ready', 'exception'] as typeof process['status'][]).includes(process?.status);
    });
  console.log(`📕 selected - 48:uploadFile.ts \n`, { selected, processingList, selectedValidList });

  const filesToUpload = selectedValidList
    .filter((it) => !!it)
    .map((it) => ({ ...it, name: `${uuidv4()}_${cleanDiacritics(it.name).replaceAll(' ', '_')}`, originalName: it.name }));
  if (!filesToUpload?.length) return;

  try {
    const requestOptions = {
      method: 'POST',
      body: JSON.stringify({
        file_names: filesToUpload.map((it) => it.name),
        action: 'UPLOAD',
      }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.getPermissionToken()}`,
      },
    };

    //? Set for ready uploading
    dispatch({
      type: 'SET_PROGRESS',
      payload: { id: selectedValidList.map((x) => x.id), progress: 0, status: 'uploading' },
    });

    const presignUrlData = await fetch(
      CONFIG_SERVER.BACKEND_URL + '/ris/attachment/v1/upload/url',
      requestOptions
    )
      .then((response) => response?.json?.())
      .catch((e) => {
        console.error(e);

        throw new Error('Error for create presignUrlData');
      });
    const presignUrls = presignUrlData?.data?.presigned_object;
    if (!presignUrls) return false;

    const metaOptions = {
      method: 'POST',
      body: JSON.stringify({
        ...currentParams,
        study_description: Array.isArray(currentParams?.study_description)
          ? currentParams?.study_description?.join(',')
          : currentParams?.study_description,
        file_infos: filesToUpload.map((it) => ({
          file_name: it.name,
          original_file_name: it.originalName,
        })),
        file_names: filesToUpload.map((it) => it.name),
        action: 'UPLOAD',
      }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.getPermissionToken()}`,
      },
    };
    const status = await fetch(
      CONFIG_SERVER.BACKEND_URL + '/ris/attachment/v1/upload-file-metadata',
      metaOptions
    )
      .then((response) => response.status)
      .catch((e) => {
        console.error(e);

        throw new Error('Failed for get status');
      });
    if (status !== 200) {
      throw new Error('Status !== 200');
    }

    const splitLimitSize = filesToUpload.map((it) => [
      {
        ...it,
        presignUrl: presignUrls.find((urlIt) => urlIt.file_name === it.name)?.url,
      },
    ]);

    // Wait for all uploads to complete before proceeding
    await Promise.allSettled(
      splitLimitSize.map((chunkFile) =>
        limit(() =>
          onUploading({
            chunk: chunkFile,
            dispatch,
          })
        )
      )
    );

    const listKey = selectedValidList.flatMap((file) => [
      file?.name,
      `backup-${file?.id}`,
      file?.id,
    ]);

    await clearListCache(listKey);

    window.removeEventListener('beforeunload', stopReload, { capture: true });

    return true;
  } catch (err) {
    console.error(err);

    throw new Error('Error for upload File');
  }
};

function onUploading({ chunk, dispatch }) {
  let dataToSend = null;
  const uploaderConfig: UploaderConfig | S3UploaderConfig = {};
  let uploader = null;
  const file = chunk[0];

  if (isPublicPortal()) {
    // S3 upload need presignUrl
    uploaderConfig.url = file.presignUrl;
    const mimeType = MIME_TO_EXTENSION[getExtensionFileName(file.name)];
    if (mimeType) {
      uploaderConfig.headers = {
        'Content-Type': mimeType,
      };
    }
    uploader = new S3Uploader(uploaderConfig);
    dataToSend = file.file;
  } else {
    uploaderConfig.method = 'POST';
    uploaderConfig.type = 'attachment';
    uploaderConfig.url = `${DEFAULT_URL_UPLOAD}/api/ris/attachment/v1/upload-files`;
    uploaderConfig.headers = {
      'Authorization': `Bearer ${keycloak.getPermissionToken()}`,
    };
    uploader = new Uploader(uploaderConfig);
    const formData = new FormData();

    // Object.entries(params).forEach(([key, value]) => {
    //   formData.append(key, value);
    // });
    formData.append('action', 'UPLOAD');
    chunk.forEach((file) => {
      formData.append('file', file.file, file.name);
    });
    dataToSend = formData;
  }

  const chunksID = chunk.map((file) => file.id);

  dispatch({ type: 'setAbort', payload: uploader.abort.bind(uploader) });

  uploader.onProgress = ({ status, percent }) => {
    let stateStatus = 'uploading';

    if (typeof status === 'number') {
      stateStatus = status === 200 ? 'success' : 'exception';
    }

    dispatch({
      type: 'SET_PROGRESS',
      payload: { id: chunksID, progress: percent, status: stateStatus },
    });
  };

  uploader.end = () => {
    dispatch({
      type: 'SET_PROGRESS',
      payload: { id: chunksID, progress: 100, status: 'success' },
    });
    console.log('SUCCESS upload chunk ', chunksID);
  };

  uploader.error = (error) => {
    if (!error) return;

    console.log('ERROR upload chunk ', chunksID);
    dispatch({
      type: 'SET_PROGRESS',
      payload: {
        id: chunksID,
        progress: 0,
        status: 'exception',
      },
    });
    return error;
  };

  return uploader.send(dataToSend);
}

export default uploadFiles;
